import { NavLink, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { supabase } from "./lib/supabaseClient.js";
import { Dashboard } from "./pages/Dashboard.js";
import { CustomersList } from "./pages/CustomersList.js";
import { CustomerDetail } from "./pages/CustomerDetail.js";
import { Devices } from "./pages/Devices.js";
import { OfflineActivation } from "./pages/OfflineActivation.js";
import { DownloadManager } from "./pages/DownloadManager.js";
import { ReleaseNotesEditor } from "./pages/ReleaseNotesEditor.js";
import { Reports } from "./pages/Reports.js";
import { AuditLog } from "./pages/AuditLog.js";
import { EmailSettingsPage } from "./pages/EmailSettings.js";

/** Small, consistent 20px stroke icons for the sidebar - no icon-font dependency. */
function Icon({ path }: { path: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {path}
    </svg>
  );
}
const icons = {
  dashboard: <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>,
  customers: <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M16 5.2a3.2 3.2 0 0 1 0 6M18 20a5.5 5.5 0 0 0-3-4.9" /></>,
  reports: <><path d="M4 20V4" /><rect x="7" y="12" width="3" height="6" rx="0.5" /><rect x="12" y="8" width="3" height="10" rx="0.5" /><rect x="17" y="5" width="3" height="13" rx="0.5" /></>,
  downloads: <><path d="M12 3v11M8 10l4 4 4-4" /><path d="M4 20h16" /></>,
  devices: <><rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4" /></>,
  offline: <><circle cx="8" cy="8" r="4.2" /><path d="M11 11l9.5 9.5M16.5 16.5l3-3M13.5 19.5l3-3" /></>,
  audit: <><path d="M4 5h11l5 5v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" /><path d="M14 5v5h5M7 13h8M7 16h5" /></>,
  email: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M4 7l8 6 8-6" /></>,
  notes: <><path d="M5 3h14a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
};

const NAV = [
  { to: "/", end: true, label: "Dashboard", icon: icons.dashboard },
  { to: "/customers", end: false, label: "Customers", icon: icons.customers },
  { to: "/devices", end: false, label: "Devices", icon: icons.devices },
  { to: "/offline-activation", end: false, label: "Offline Activation", icon: icons.offline },
  { to: "/reports", end: false, label: "Reports", icon: icons.reports },
  { to: "/downloads", end: false, label: "Downloads", icon: icons.downloads },
  { to: "/release-notes", end: false, label: "Release notes", icon: icons.notes },
  { to: "/audit-log", end: false, label: "Audit log", icon: icons.audit },
  { to: "/email-settings", end: false, label: "Email settings", icon: icons.email },
];

/** The admin console shell: a left-sidebar portal. Rendered only once AuthGate
 * has confirmed (server-side, GET /api/me) the signed-in user is an admin. */
export function AdminApp({ email }: { email: string }) {
  return (
    <div className="portal">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-lockup">
            <img src="/logo.png" alt="" />
            <strong>AudioSw<span className="y">y</span>tch</strong>
          </div>
          <span className="brand-context">Console</span>
          <div className="brand-rule" />
        </div>

        <nav className="sidebar-nav">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              <span className="nav-icon"><Icon path={item.icon} /></span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="sidebar-email" title={email}>{email}</div>
          <button className="secondary" onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </aside>

      <main className="portal-main">
        <div className="app-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/customers" element={<CustomersList />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/offline-activation" element={<OfflineActivation />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/downloads" element={<DownloadManager />} />
            <Route path="/release-notes" element={<ReleaseNotesEditor />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/email-settings" element={<EmailSettingsPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
